import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { getRemainingSearches, setPremiumUser, isPremiumUser } from '../services/searchLimit';

const BYPASS_PASSWORD = "1102562025LP"; // The required password
const MAX_FREE_SEARCHES = 3;
const MAX_PREMIUM_SEARCHES = 100;

const SearchLimitBanner = () => {
  const [remaining, setRemaining] = useState(getRemainingSearches());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const premium = isPremiumUser();
  const max = premium ? MAX_PREMIUM_SEARCHES : MAX_FREE_SEARCHES;

  useEffect(() => {
    // Update remaining searches when component mounts
    setRemaining(getRemainingSearches());

    // Listen for search count updates
    const handleSearchCountUpdate = () => {
      setRemaining(getRemainingSearches());
    };

    window.addEventListener('searchCountUpdated', handleSearchCountUpdate);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('searchCountUpdated', handleSearchCountUpdate);
    };
  }, []);

  const handleBypassClick = () => {
    setDialogOpen(true);
    setPassword('');
    setPasswordError('');
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setPassword('');
    setPasswordError('');
  };

  const handlePasswordSubmit = () => {
    if (password === BYPASS_PASSWORD) {
      setPremiumUser();
      setRemaining(getRemainingSearches());
      handleDialogClose();
    } else {
      setPasswordError('Incorrect password');
    }
  };

  return (
    <Box sx={{
      p: 3,
      mb: 3,
      borderRadius: 3,
      background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      alignItems: { xs: 'flex-start', sm: 'center' },
      justifyContent: 'space-between',
      gap: 2
    }}>
      <Box>
        <Typography variant="subtitle1" sx={{ 
          fontWeight: 600,
          background: remaining > 0 
            ? 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)'
            : '#ef4444',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          {remaining > 0
            ? `${remaining} of ${max} ${premium ? 'Premium' : 'Free'} Searches Remaining`
            : `${premium ? 'Premium' : 'Free'} Search Limit Reached`}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {remaining > 0
            ? `You have ${remaining} ${premium ? 'premium' : 'free'} searches left.`
            : premium
              ? `You have used all your premium searches.`
              : `You have used all your free searches. Enter your premium password or get premium access.`}
        </Typography>
      </Box>
      {remaining === 0 && (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleBypassClick}
            sx={{
              height: 48,
              px: 4,
              background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #16A34A 0%, #15803D 100%)',
              },
              whiteSpace: 'nowrap'
            }}
          >
            Enter Premium Password
          </Button>
          <Button
            variant="contained"
            onClick={() => window.location.href = 'https://creatoriq.app'}
            sx={{
              height: 48,
              px: 4,
              background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #7C3AED 0%, #DB2777 100%)',
              },
              whiteSpace: 'nowrap'
            }}
          >
            Get Premium Access
          </Button>
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Enter Premium Password</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Password"
            type="password"
            fullWidth
            value={password}
            onChange={e => setPassword(e.target.value)}
            error={!!passwordError}
            helperText={passwordError}
            onKeyDown={e => {
              if (e.key === 'Enter') handlePasswordSubmit();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handlePasswordSubmit} variant="contained">Submit</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SearchLimitBanner;
